<template>
  <div class="d-flex flex-column view-wrapper">
    
    <v-card class="elevation-1 rounded-0 mb-1 flex-shrink-0" color="primary">
      <v-card-text class="pa-2 d-flex justify-space-between align-center">
        <div>
          <div class="text-subtitle-1 font-weight-bold white--text">
            Resumo de Vendas por Cidade
          </div>
          <div class="caption white--text">
            {{ periodoFormatado }}
          </div>
        </div>
        
        <v-chip small color="white" outlined>
          {{ items.length }} Cidades
        </v-chip>
      </v-card-text>
    </v-card>

    <div class="content-container flex-grow-1 elevation-1 white" style="position: relative; min-height: 0;">
      
      <div v-if="loading" class="d-flex justify-center align-center fill-height">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <div v-else-if="items.length === 0" class="d-flex justify-center align-center fill-height grey--text">
        <div class="text-center">
          <v-icon size="48" color="grey lighten-2">mdi-chart-bar-off</v-icon>
          <p class="mt-2">Nenhuma venda encontrada no período.</p>
        </div>
      </div>

      <simplebar
        v-else
        :auto-hide="false"
        class="table-scroll-container"
        style="height: 100%; max-width: 100%;"
      >
        <table class="table-custom">
          <colgroup>
            <col style="width: 150px; min-width: 150px;" /> 
            <col style="width: 70px;" />  
            <col style="width: 70px;" />  
            <col style="width: 70px;" />  
            <col style="width: 60px;" />  
            <col style="width: 50px;" />  
            <col style="width: 90px;" />  
            <col style="width: 70px;" />  
          </colgroup>
          <thead class="table-header sticky-header">
            <tr>
              <th class="text-left pl-3">Cidade</th>
              <th class="text-right">Mês Ant.</th>
              <th class="text-right">Mês Atual</th>
              <th class="text-right">%Atual</th>
              <th class="text-center">Carteira</th>
              <th class="text-center">Visitas</th>
              <th class="text-center">Convertidos</th>
              <th class="text-center">Não Convertidos</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in items"
              :key="item.cidade"
              class="table-row"
              :class="{ 'linha-alternada': index % 2 !== 0 }"
            >
              <td class="text-left pl-3 font-weight-medium text-truncate" style="max-width: 150px;" :title="item.cidade">
                {{ item.cidade }}
              </td>
              
              <td class="text-right">
                {{ formatarValor(item.mesAnterior) }}
              </td>
              
              <td class="text-right">
                {{ formatarValor(item.mesAtual) }}
              </td>
              
              <td class="text-right font-weight-bold" :class="getCorPercentual(item.percentualAtual)">
                {{ item.percentualAtual.toFixed(1) }}%
              </td>

              <td class="text-center">
                {{ item.carteira }}
              </td>
              <td class="text-center">
                {{ item.visitas }}
              </td>
              <td class="text-center">
                {{ item.convertidos }}
              </td>
              <td class="text-center">
                {{ item.naoConvertidos }}
              </td>
            </tr>
          </tbody>
        </table>
      </simplebar>
    </div>

    <v-card class="elevation-4 rounded-0 flex-shrink-0 grey lighten-4">
      <v-card-text class="pa-2">
        <v-row no-gutters justify="space-between" align="center">
          <v-col cols="3" class="text-center">
            <div class="caption grey--text text--darken-2">Mês Ant.</div>
            <div class="font-weight-bold black--text body-2">
              {{ formatarValor(subtotais.mesAnterior) }}
            </div>
          </v-col>
          
          <v-col cols="3" class="text-center border-left border-right">
            <div class="caption grey--text text--darken-2">Mês Atual</div>
            <div class="font-weight-bold primary--text body-2">
              {{ formatarValor(subtotais.mesAtual) }}
            </div>
          </v-col>
          
          <v-col cols="3" class="text-center">
            <div class="caption grey--text text--darken-2">Crescimento</div>
            <div class="font-weight-black body-2" :class="getCorPercentual(subtotais.percentualAtual)">
              {{ subtotais.percentualAtual.toFixed(1) }}%
            </div>
          </v-col>

          <v-col cols="3" class="text-center border-left">
            <div class="caption grey--text text--darken-2">visitas</div>
            <div class="font-weight-black body-2 black--text">
              {{ subtotais.visitas }}
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

  </div>
</template>

<script>
import simplebar from 'simplebar-vue';
import 'simplebar-vue/dist/simplebar.min.css';
import db from '@/plugins/dexie';

export default {
  name: 'ViewPesquisaVendasResumo',

  components: {
    simplebar,
  },

  data() {
    return {
      loading: false,
      items: [],
      periodoFormatado: '',
      idVendedor: 0,
    };
  },

  computed: {
    subtotais() {
      const totalMesAnterior = this.items.reduce((acc, item) => acc + item.mesAnterior, 0);
      const totalMesAtual = this.items.reduce((acc, item) => acc + item.mesAtual, 0);
      const totalVisitas = this.items.reduce((acc, item) => acc + item.visitas, 0);
      
      let percentualTotal = 0;
      if (totalMesAnterior > 0) {
        percentualTotal = ((totalMesAtual / totalMesAnterior) - 1) * 100;
      } else if (totalMesAtual > 0) {
        percentualTotal = 100;
      }

      return {
        mesAnterior: totalMesAnterior,
        mesAtual: totalMesAtual,
        percentualAtual: percentualTotal,
        visitas: totalVisitas
      };
    },
  },
  
  created() {
    if (this.$route.query.idVendedor) {
      this.idVendedor = parseInt(this.$route.query.idVendedor, 10);
    }
    
    const hoje = new Date();
    this.periodoFormatado = hoje.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    
    this.carregarDados();
  },

  methods: {
    formatarValor(valor) {
      if (!valor) return '0,00';
      return valor.toLocaleString('pt-BR', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
    },
    
    getCorPercentual(valor) {
      if (valor > 0) return 'green--text text--darken-2';
      if (valor < 0) return 'red--text text--darken-2';
      return 'grey--text';
    },

    async carregarDados() {
      this.loading = true;
      this.items = [];

      try {
        const parametro = await db.parametro.toCollection().first();
        const idVendedorLogado = parametro && parametro.idvendedorlogado 
          ? parseInt(parametro.idvendedorlogado, 10) 
          : 0;
        if (idVendedorLogado === 0) {
          console.warn("Nenhum vendedor logado encontrado na tabela de parâmetros.");
          return; 
        }

        this.idVendedor = idVendedorLogado;

        const agora = new Date();
        const anoAtual = agora.getFullYear();
        const mesAtual = agora.getMonth();
        
        const inicioMesAtual = new Date(anoAtual, mesAtual, 1);
        const inicioMesAnterior = new Date(anoAtual, mesAtual - 1, 1);
        const fimMesAnterior = new Date(anoAtual, mesAtual, 0, 23, 59, 59);
        
        const dataInicioStr = inicioMesAnterior.toISOString();
        const dataFimStr = new Date(anoAtual, mesAtual + 1, 0, 23, 59, 59).toISOString();

        let collection = db.venda
          .where('data')
          .between(dataInicioStr, dataFimStr, true, true)
          .and(venda => venda.idvendedor === idVendedorLogado && venda.cancelado !== 'S' && venda.idempresa == 1);

        const vendasFiltradas = await collection.toArray();

        const todosClientes = await db.cliente.toArray();
        const clientesDoVendedor = todosClientes.filter(c => Number(c.idvendedor) === idVendedorLogado);

        const clientMap = new Map();
        todosClientes.forEach(c => {
          clientMap.set(Number(c.idcliente), c);
          clientMap.set(String(c.idcliente), c);
        });

        const todasVisitas = await db.visita
          .where('datavisita')
          .between(dataInicioStr, dataFimStr, true, true)
          .toArray();

        const visitasFiltradas = todasVisitas.filter(v => Number(v.idvendedor) === idVendedorLogado);

        const agrupamento = {};
        const chavesCidades = {};

        const obterNomeCidadeNormalizado = (cidadeRaw) => {
          const cidade = (cidadeRaw || 'Indefinida').trim();
          const cidadeUpper = cidade.toUpperCase();
          if (!chavesCidades[cidadeUpper]) {
            chavesCidades[cidadeUpper] = cidade;
          }
          return chavesCidades[cidadeUpper];
        };

        const obterEstruturaCidade = () => ({
          mesAnterior: 0,
          mesAtual: 0,
          carteira: 0,
          visitas: 0,
          convertidos: 0,
          naoConvertidos: 0
        });

        const vendasProcessadasAtual = new Set();
        const vendasProcessadasAnterior = new Set();

        vendasFiltradas.forEach(venda => {
          const dataVenda = new Date(venda.data);
          const nomeCidade = obterNomeCidadeNormalizado(venda.cidade);
          const idVenda = venda.idvenda;

          if (!agrupamento[nomeCidade]) {
            agrupamento[nomeCidade] = obterEstruturaCidade();
          }

          const valorLiquido = parseFloat(venda.vlrliquidovenda) || 0;

          if (dataVenda >= inicioMesAtual) {
            const chaveControle = `${nomeCidade}_${idVenda}`;
            if (!vendasProcessadasAtual.has(chaveControle)) {
              agrupamento[nomeCidade].mesAtual += valorLiquido;
              vendasProcessadasAtual.add(chaveControle);
            }
          } else if (dataVenda >= inicioMesAnterior && dataVenda <= fimMesAnterior) {
            const chaveControle = `${nomeCidade}_${idVenda}`;
            if (!vendasProcessadasAnterior.has(chaveControle)) {
              agrupamento[nomeCidade].mesAnterior += valorLiquido;
              vendasProcessadasAnterior.add(chaveControle);
            }
          }
        });

        clientesDoVendedor.forEach(cliente => {
          const nomeCidade = obterNomeCidadeNormalizado(cliente.cidade);

          if (!agrupamento[nomeCidade]) {
            agrupamento[nomeCidade] = obterEstruturaCidade();
          }

          agrupamento[nomeCidade].carteira += 1;
        });

        visitasFiltradas.forEach(visita => {
          const cliente = clientMap.get(Number(visita.idcliente)) || clientMap.get(String(visita.idcliente));
          const nomeCidade = obterNomeCidadeNormalizado(cliente ? cliente.cidade : 'Indefinida');

          if (!agrupamento[nomeCidade]) {
            agrupamento[nomeCidade] = obterEstruturaCidade();
          }

          agrupamento[nomeCidade].visitas += 1;

          const isConvertido = !!visita.VEVIdPedidoVenda;
          if (isConvertido) {
            agrupamento[nomeCidade].convertidos += 1;
          } else {
            agrupamento[nomeCidade].naoConvertidos += 1;
          }
        });

        this.items = Object.keys(agrupamento).map(cidade => {
          const dados = agrupamento[cidade];
          
          let percentual = 0;
          if (dados.mesAnterior > 0) {
            percentual = ((dados.mesAtual / dados.mesAnterior) - 1) * 100;
          } else if (dados.mesAtual > 0) {
            percentual = 100;
          }

          return {
            cidade: cidade,
            mesAnterior: dados.mesAnterior,
            mesAtual: dados.mesAtual,
            percentualAtual: percentual,
            carteira: dados.carteira,
            visitas: dados.visitas,
            convertidos: dados.convertidos,
            naoConvertidos: dados.naoConvertidos
          };
        });

        this.items.sort((a, b) => a.cidade.localeCompare(b.cidade));

      } catch (error) {
        console.error("Erro ao carregar resumo de vendas:", error);
      } finally {
        this.loading = false;
      }
    }
  },
};
</script>

<style scoped>

.view-wrapper {
  height: calc(100vh - 100px);
  background-color: white;
}

.table-custom {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto; 
  min-width: 950px;
}

.table-header th {
  background-color: #f5f5f5;
  color: #616161;
  padding: 10px 4px;
  font-size: 0.75rem;
  text-transform: uppercase;
  border-bottom: 2px solid #e0e0e0;
  white-space: nowrap;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 2; 
  box-shadow: 0 2px 2px -1px rgba(0,0,0,0.1);
}

.table-row td {
  padding: 8px 4px;
  border-bottom: 1px solid #eeeeee;
  font-size: 0.85rem;
  color: #424242;
  white-space: nowrap;
}

.linha-alternada {
  background-color: #fafafa;
}

.border-left {
  border-left: 1px solid #e0e0e0;
}
.border-right {
  border-right: 1px solid #e0e0e0;
}

/* Configuração do Simplebar para scroll horizontal */
.table-scroll-container {
  overflow-x: auto;
}

.table-scroll-container ::v-deep .simplebar-scrollbar::before {
  background: rgba(76, 111, 175, 0.4) !important;
}
</style>